import base64url from 'base64url';
import { logger } from '../util/Logger';
import { StreamKey } from '../util/Serialization/StreamKey';
import { equal } from '../util/Util/Equal';
import { Dir, Entry, EntryType, Path } from './Entry';

enum EntryToFrom {
  From = 0,
  To = 1,
}

export enum Operation {
  Undefined = 0,
  Create,
  Delete,
  Change,
  Touch,
  Move,
  Copy,
  Rename,
}

export interface Result {
  path: Path;
  operation: Operation;
  origins: Array<Path>;
}

export interface Summary {
  path: Path;
  operation: Operation;
  results: Result[];

  // The amount of results that are not part of the summary. This is used for
  // caching the summary results (at the api). This because the 'results' array
  // could get very large. If a user is interested in all the results then the
  // user should generate the diff.
  cutoffCount: number;
}

// JSON.parse-ed version of a diff result. For communication from/to api.
export interface DiffResultJson {
  path: string;
  operation: Operation;
  origins: Array<string>;
}

// JSON.parse-ed version of a diff summary. For communication from/to api.
export interface DiffSummaryJson {
  path: string;
  operation: Operation;
  results: DiffResultJson[];
  cutoffCount: number;
}

// Convert JSON diff summaries to actual Diff summaries.
export const fromJsonDiffSummary = (data: DiffSummaryJson[]): Summary[] => {
  return data.map((summary: DiffSummaryJson): Summary => {
    return {
      path: new Path(summary.path),
      operation: summary.operation,
      results: summary.results.map((result: DiffResultJson): Result => {
        return {
          path: new Path(result.path),
          operation: result.operation,
          origins: result.origins.map(path => new Path(path)),
        };
      }),
      cutoffCount: summary.cutoffCount,
    };
  });
};

// Filter out the Touch results if we have other types of changes like Added
// or Changed. This shows less clutter of meaningless Touch changes in the gui.
// An example:
// You have a commit with the following diff:
// ==============================
// Alice on 2023-11-28:
// - Alice changed a/foo.txt
// - Alice touched a
// ==============================
// Then you want to remove the 'Alice touched a' line because it doesn't add
// much value for the user. But lets look at the following example:
// ==============================
// Bob on 2023-11-28:
// - Bob touched a
// ==============================
// In the commit above you want to keep showing the touched diff result because
// otherwise the diff will be empty resulting in confused users.
// Commits with only a touch event could theoretically occur but we have
// mechanisms in place to prevent them from being generated.
export const filterTouches = (results: Summary[]): Summary[] => {
  const filter = results.filter(res => res.operation !== Operation.Touch);
  return filter.length > 0 ? filter : results;
};

// Generate a diff between two dirs. Use Diff::diff();
export class Diff {
  static async dirEntries(entry: Dir): Promise<Map<string, Entry>> {
    const map = new Map<string, Entry>();
    const children = await entry.entries();
    for (const child of children) {
      const name: Uint8Array = child.encryptedName;
      const hexName = Buffer.from(name).toString('hex');
      map.set(hexName, child);
    }
    return map;
  }

  // Recursively list entries from a given directory.
  // @param path the path to the entry (includes the name of the entry)
  // @param entry the Dir entry
  // @returns array of [Path, Entry] pairs
  static async recursiveListEntries(path: Path, entry: Dir): Promise<[Path, Entry][]> {
    const entries: [Path, Entry][] = [];
    for (const child of await entry.entries()) {
      const childPath = path.clone();
      childPath.push(child.encryptedName);
      entries.push([childPath, child]);
      if (child.type === EntryType.Dir) {
        entries.push(...(await this.recursiveListEntries(childPath, child.toDir())));
      }
    }
    return entries;
  }

  // Finds the basic differences between 2 dirs.
  // A basic diff means that a entry on a path is not the same anymore.
  // The following is returned for each difference:
  //  - The full path of the entry that differs
  //  - The entry as it was in the 'before' state
  //  - The entry as it is in the 'after' state
  // When both before and after are not nullptr then the entry is change.
  // When before is undefined and after is not undefined then an entry is added at that location.
  // When before is not undefined and after is undefined then an entry is removed at that location.
  static async naiveDiff(
    fromDir: Dir,
    toDir: Dir,
    path: Path = new Path(),
    results: Map<Path, { from?: Entry; to?: Entry }> | undefined = undefined,
  ): Promise<Map<Path, { from?: Entry; to?: Entry }>> {
    if (results === undefined) {
      results = new Map<Path, { from?: Entry; to?: Entry }>();
    }

    if (fromDir.key !== undefined && toDir.key !== undefined && fromDir.key.isEqual(toDir.key)) {
      return Promise.resolve(results);
    }

    if (fromDir.mod.valueOf() !== toDir.mod.valueOf() && !path.isEqualTo(new Path())) {
      results.set(path.clone(), { from: fromDir, to: toDir });
    }

    const toEntries = await Diff.dirEntries(toDir);
    const fromEntries = await Diff.dirEntries(fromDir);

    // Combine sets of keys
    const entryKeys = Array.from(fromEntries.keys());
    entryKeys.push(...toEntries.keys());
    const entries = entryKeys.filter((element, index, array) => array.indexOf(element) === index);
    for (const iterator of entries) {
      const toContains = toEntries.has(iterator);
      const fromContains = fromEntries.has(iterator);

      const subPath = path.clone();
      if (toContains) {
        subPath.push(toEntries.get(iterator)!.encryptedName);
      } else if (fromContains) {
        subPath.push(fromEntries.get(iterator)!.encryptedName);
      }
      if (fromContains && toContains) {
        const fromEntry = fromEntries.get(iterator)!;
        const toEntry = toEntries.get(iterator)!;
        if (fromEntry.type === EntryType.Dir && toEntry.type === EntryType.Dir) {
          await Diff.naiveDiff(fromEntry.toDir(), toEntry.toDir(), subPath, results);
          continue;
        }
        if (StreamKey.areEqual(fromEntry.key, toEntry.key) && fromEntry.mod.valueOf() === toEntry.mod.valueOf()) {
          continue;
        }
        results.set(subPath, { from: fromEntry, to: toEntry });
      } else if (!fromContains && toContains) {
        const toEntry = toEntries.get(iterator)!;
        results.set(subPath, { from: undefined, to: toEntry });
        if (toEntry.type === EntryType.Dir) {
          // Iterate over the new folder as it may already contain items
          const children = await toEntry.toDir().entries();
          if (!children) {
            continue;
          }
          for (const child of children) {
            const childPath = subPath.clone();
            childPath.push(child.encryptedName);
            results.set(childPath, { from: undefined, to: child });
            if (child.type === EntryType.Dir) {
              const childEntries = await this.recursiveListEntries(childPath, child.toDir());
              for (const entry of childEntries) {
                results.set(entry[0], { from: undefined, to: entry[1] });
              }
            }
          }
        }
      } else if (fromContains && !toContains) {
        const fromEntry = fromEntries.get(iterator)!;
        results.set(subPath, { from: fromEntry, to: undefined });
        if (fromEntry.type === EntryType.Dir) {
          // Iterate over the deleted folder as the contents are also deleted.
          const children = await fromEntry.toDir().entries();
          if (!children) {
            continue;
          }
          for (const child of children) {
            const childPath = subPath.clone();
            childPath.push(child.encryptedName);
            results.set(childPath, { from: child, to: undefined });
            if (child.type === EntryType.Dir) {
              const childEntries = await this.recursiveListEntries(childPath, child.toDir());
              for (const entry of childEntries) {
                results.set(entry[0], { from: entry[1], to: undefined });
              }
            }
          }
        }
      }
    }
    return results;
  }

  /**
   * Iterates over the entries of a given directory. The method records the
   * Path, locator and Dir (if any). And returns the array.
   */
  static async gatherEntriesFromDir(dir: Dir, path: Path): Promise<Array<[Path, string, Dir | undefined]>> {
    const entries: Array<[Path, string, Dir | undefined]> = [];
    for (const entry of await dir.clone().toDir().entries()) {
      const modifiedPath = path.clone();
      modifiedPath.push(entry.encryptedName);
      const locator = entry.key ? base64url.encode(Buffer.from(entry.key.getLocator())) : '';
      const dir = entry.type === EntryType.Dir ? entry.toDir() : undefined;
      entries.push([modifiedPath, locator, dir]);
    }
    return entries;
  }

  /**
   * Convert a basicDiff to an array of locators. Only locators where the
   * fromEntry is undefined and the toEntry holds a StreamKey are selected.
   */
  static basicDiffToLocators(basicDiff: Map<Path, { from?: Entry; to?: Entry }>): Array<string> {
    const result: Array<string> = [];
    for (const item of basicDiff.values()) {
      const fromEntry = item.from;
      const toEntry = item.to;
      if (fromEntry === undefined) {
        if (toEntry === undefined) {
          continue;
        }
        const streamKey = toEntry.key;
        if (streamKey === undefined) {
          continue;
        }
        const locator = base64url.encode(Buffer.from(streamKey.getLocator()));
        result.push(locator);
      }
    }
    return result;
  }

  // Gathers all the origins for a dir - path combination.
  static async originHash(dir: Dir, path: Path, basicDiff: Map<Path, { from?: Entry; to?: Entry }>): Promise<Map<string, Path[]>> {
    const locatorsOfInterest = this.basicDiffToLocators(basicDiff);
    const result = new Map<string, Path[]>();
    const entries = await this.gatherEntriesFromDir(dir, path);
    const directories: Array<[Dir, Path]> = [];
    while (entries.length > 0 || directories.length > 0) {
      while (directories.length > 0) {
        const directory = directories.pop();
        if (directory !== undefined) {
          const newEntries = await this.gatherEntriesFromDir(directory[0], directory[1]);
          entries.push(...newEntries);
        }
      }
      const entry = entries.pop();
      if (entry !== undefined) {
        if (entry[1] !== '' && locatorsOfInterest.includes(entry[1])) {
          result.set(entry[1], [entry[0]]);
        }
        if (entry[2] !== undefined) {
          directories.push([entry[2].clone().toDir(), entry[0]]);
        }
      }
    }
    return result;
  }

  // Preprocesses a single entry. It tries to determine if this was a
  // move/rename or create, remove.
  static preprocessEntry(
    sourcePath: Path,
    entry: Entry,
    typeOfEntry: EntryToFrom,
    basicDiff: Map<Path, { from?: Entry; to?: Entry }>,
  ): [Operation, Path, Path] {
    for (const [path, value] of basicDiff) {
      let compareEntry: Entry | undefined;
      if (typeOfEntry === EntryToFrom.From) {
        compareEntry = value.to;
      } else {
        compareEntry = value.from;
      }
      if (compareEntry === undefined) {
        continue;
      }
      const entryName = base64url.encode(Buffer.from(entry.encryptedName));
      const compareName = base64url.encode(Buffer.from(compareEntry.encryptedName));
      if (
        entry.mod.valueOf() === compareEntry.mod.valueOf() &&
        entry.size === compareEntry.size &&
        entry.type === compareEntry.type &&
        entryName === compareName
      ) {
        if (typeOfEntry === EntryToFrom.From) {
          return [Operation.Move, path, sourcePath];
        } else {
          return [Operation.Move, sourcePath, path];
        }
      }

      if (
        entry.mod.valueOf() === compareEntry.mod.valueOf() &&
        entry.size === compareEntry.size &&
        entry.type === compareEntry.type &&
        entryName !== compareName
      ) {
        try {
          const sourceParent = sourcePath.clone();
          sourceParent.pop();
          const destinationParent = path.clone();
          destinationParent.pop();
          if (sourceParent.isEqualTo(destinationParent)) {
            if (typeOfEntry === EntryToFrom.From) {
              return [Operation.Rename, path, sourcePath];
            } else {
              return [Operation.Rename, sourcePath, path];
            }
          }
        } catch {
          // Failure doesn't matter here.
        }
      }
    }
    if (typeOfEntry === EntryToFrom.From) {
      return [Operation.Delete, sourcePath, sourcePath];
    }
    return [Operation.Create, sourcePath, sourcePath];
  }

  // Preprocesses the diff generated from the `naiveDiff` method.
  // It processes the diff into newly added content keys, renamed paths
  // and removed paths (i.e. the set arguments).
  // Note: this is not a regular input->output method, it modifies the
  // inputs.
  static dirPreprocess(basicDiff: Map<Path, { from?: Entry; to?: Entry }>): [StreamKey[], Path[], Path[], Result[], Map<Entry, Path>] {
    const newContentKeys: StreamKey[] = [];
    const renamedPaths: Path[] = [];
    const removedPaths: Path[] = [];
    const changes: Result[] = [];
    const newPaths: Map<Entry, Path> = new Map();

    for (const [path, value] of basicDiff) {
      const fromEntry = value.from;
      const toEntry = value.to;

      if (toEntry !== undefined && fromEntry !== undefined) {
        continue;
      }
      if (toEntry !== undefined) {
        // Add the 'new' streamKey to the newContentKeys
        if (toEntry.key) {
          newContentKeys.push(toEntry.key);
          newPaths.set(toEntry, path);
        }
      }

      // Here we will try to find the paths that have been renamed.
      // Since an empty file or dir does not have a valid content key
      // we cannot rely on that mechanism to find out paths that have been
      // renamed. The alternative is to find an entry in 'from' paths list
      // that shares the same modification time and is also empty.
      const result = this.preprocessEntry(
        path,
        fromEntry !== undefined ? fromEntry! : toEntry!,
        fromEntry !== undefined ? EntryToFrom.From : EntryToFrom.To,
        basicDiff,
      );
      let index = -1;
      switch (result[0]) {
        case Operation.Move:
        case Operation.Rename:
          if (!renamedPaths.includes(result[1])) {
            renamedPaths.push(result[1]);
          }
          if (!renamedPaths.includes(result[2])) {
            renamedPaths.push(result[2]);
          }
          // Changes is an Array, not a set. We need to check if our change is
          // already in there.
          index = changes.findIndex(element => {
            return element.path.isEqualTo(result[1]);
          });
          if (index === -1) {
            changes.push({ path: result[1], operation: result[0], origins: [result[2]] });
          }
          break;
        case Operation.Delete:
          removedPaths.push(result[1]);
          break;
        default:
          break;
      }
    }
    return [newContentKeys, renamedPaths, removedPaths, changes, newPaths];
  }

  static async findCopies(newPaths: Map<Entry, Path>, toDir: Dir, path: Path = new Path()): Promise<Map<StreamKey, [Path]>> {
    const map: Map<StreamKey, [Path]> = new Map();

    const entries: Array<[Entry, Path]> = (await toDir.entries()).map(entry => [entry, path]);
    const directories: Array<[Dir, Path]> = [];
    while (entries.length > 0 || directories.length > 0) {
      while (directories.length > 0) {
        const directory = directories.pop();
        if (directory !== undefined) {
          const newEntries: Array<[Entry, Path]> = (await directory[0].entries()).map(entry => [entry, directory[1]]);
          entries.push(...newEntries);
        }
      }
      const entry = entries.pop();
      if (entry === undefined) {
        continue;
      }
      const subPath = entry[1].clone();
      subPath.push(entry[0].encryptedName);
      if (newPaths.has(entry[0])) {
        const comparePath = newPaths.get(entry[0])!;
        if (!comparePath.isEqualTo(subPath)) {
          const compareStreamKey = entry[0].key!;
          if (map.has(compareStreamKey)) {
            map.get(compareStreamKey)!.push(subPath);
          } else {
            map.set(compareStreamKey, [subPath]);
          }
        }
      } else {
        for (const [key, comparePath] of newPaths) {
          if (subPath.isEqualTo(comparePath)) {
            continue;
          }
          const isCopy = await this.isCopy(key, entry[0]);
          if (isCopy) {
            const streamKey = key.key!;
            if (map.has(streamKey)) {
              map.get(streamKey)!.push(subPath);
            } else {
              map.set(streamKey, [subPath]);
            }
          }
        }
      }
      if (entry[0].type === EntryType.Dir) {
        const subDir = entry[0].clone().toDir();
        directories.push([subDir, subPath]);
      }
    }
    return map;
  }

  static gatherOrigins(origins: Map<string, Path[]>, fromEntry?: Entry, toEntry?: Entry): Path[] {
    if (fromEntry === undefined) {
      if (toEntry === undefined) {
        return [];
      }
      const streamKey = toEntry.key;
      if (streamKey !== undefined) {
        const locator = base64url.encode(Buffer.from(streamKey.getLocator()));
        return origins.has(locator) ? origins.get(locator)! : [];
      }
    }
    return [];
  }

  static isMove(newContentKeys: StreamKey[], fromEntry?: Entry): boolean {
    if (fromEntry === undefined) {
      return false;
    }
    const fromStreamKey = fromEntry.key;
    let move = false;
    if (fromStreamKey !== undefined) {
      // newContentKeys.includes does not work as we are dealing with
      // UInt8Arrays.
      const index = newContentKeys.findIndex(element => {
        return element.isEqual(fromStreamKey);
      });

      // An index of -1 means it is not found.
      if (index !== -1) {
        move = true;
      }
    }
    return move;
  }

  static async isCopy(left: Entry, right: Entry): Promise<boolean> {
    if (left.type !== right.type) {
      return false;
    }
    const leftStreamKey = left.key;
    const rightStreamKey = right.key;
    if (leftStreamKey !== undefined && rightStreamKey !== undefined && leftStreamKey.isEqual(rightStreamKey)) {
      return true;
    }

    if (left.type === EntryType.Dir && right.type === EntryType.Dir) {
      const leftDir = left.toDir();
      const rightDir = right.toDir();
      const leftEntries = await leftDir.entries();
      const rightEntries = await rightDir.entries();

      if (leftEntries.length !== rightEntries.length) {
        return false;
      }
      let isCopy = true;
      for (const entry of leftEntries) {
        const iter = rightEntries.find(rightEntry => equal(rightEntry.encryptedName, entry.encryptedName));
        if (iter === undefined) {
          isCopy = false;
          break;
        }
        const otherEntry = iter;
        if (!this.isCopy(entry, otherEntry)) {
          isCopy = false;
          break;
        }
      }
      return isCopy;
    }
    return false;
  }

  static operationWithoutOrigins(newContentKeys: StreamKey[], fromEntry?: Entry, toEntry?: Entry): Operation {
    let operation = Operation.Undefined;
    if (fromEntry === undefined) {
      operation = Operation.Create;
    } else if (toEntry === undefined && this.isMove(newContentKeys, fromEntry)) {
      // We ignore file removes when part of a move operation.
      operation = Operation.Move;
    } else if (toEntry === undefined && !this.isMove(newContentKeys, fromEntry)) {
      // Delete
      operation = Operation.Delete;
    } else if (fromEntry.type === EntryType.File && toEntry!.type !== EntryType.File) {
      // Dir<->File morph.
      operation = Operation.Change;
    } else if (fromEntry.type === EntryType.File) {
      // Chances of the content key staying the same but the filesize changing are extremely small (non-existent).
      // We still support this case because we need the DirDiff to notice filesize changes independent of
      // content key changes, for instance when we're correcting an incorrect filesize.
      if (fromEntry.key !== toEntry!.key || fromEntry.size.valueOf() !== toEntry!.size.valueOf()) {
        operation = Operation.Change;
      } else {
        operation = Operation.Touch;
      }
    } else {
      // Dir
      if (fromEntry.mod.valueOf() !== toEntry!.mod.valueOf()) {
        operation = Operation.Touch;
      }
    }
    return operation;
  }

  static isOverlapping(originFilePaths: Path[], removedPaths: Path[]): boolean {
    let overlaps = false;
    for (const originPath of originFilePaths) {
      for (const removedPath of removedPaths) {
        if (removedPath.overlaps(originPath)) {
          overlaps = true;
          break;
        }
      }
      if (overlaps) {
        break;
      }
    }
    return overlaps;
  }

  static filterNonOverlapping(originFilePaths: Path[], removedPaths: Path[]): Path[] {
    let copyOrigin = originFilePaths;
    for (const originPath of originFilePaths) {
      let overlaps = false;
      for (const removedPath of removedPaths) {
        if (removedPath.overlaps(originPath)) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        copyOrigin = copyOrigin.filter(element => !element.isEqualTo(originPath));
      }
    }
    return copyOrigin;
  }

  static renameParentDirectory(_: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;

    // Go over the moves to see if their parent dir has been renamed.
    const moves: Result[] = [];
    const renames: Result[] = [];
    for (const change of changes) {
      switch (change.operation) {
        case Operation.Move:
          moves.push(change);
          break;
        case Operation.Rename:
          renames.push(change);
          break;
        default:
          break;
      }
    }
    for (const change of moves) {
      // If also renamed skip
      let rename = false;
      const changePath = change.path.clone();
      const name = base64url.encode(Buffer.from(changePath.pop()));
      for (const origin of change.origins) {
        const originPath = origin.clone();
        const oldName = base64url.encode(Buffer.from(originPath.pop()));
        if (name !== oldName) {
          rename = true;
          break;
        }
      }
      if (rename) {
        continue;
      }

      // Check if parent directory is part of a rename.
      const parentPath = change.path.clone();
      parentPath.pop();
      for (const rename of renames) {
        if (parentPath.isEqualTo(rename.path)) {
          // Remove entry from copy
          copy = copy.filter(element => element !== change);
          break;
        }
      }
    }
    return copy;
  }

  static directoryRenames(basicDiff: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;
    const candidates: Result[] = [];

    // Go over the moves to see if there are candidates.
    for (const change of changes) {
      if (change.operation === Operation.Delete) {
        // If this is a deleted directory without any children being
        // removed it is a candidate for a rename
        // or move.
        const path = change.path.clone();
        // Does this work?
        const toEntry = basicDiff.get(path)!;
        if (toEntry !== undefined && toEntry.to !== undefined && toEntry.to.type === EntryType.Dir) {
          continue;
        }
        let childDelete = false;
        for (const child of changes) {
          // Does this comparison work?
          if (child === change) {
            continue;
          }
          if (child.operation === Operation.Delete && child.path.overlaps(path)) {
            childDelete = true;
            break;
          }
          if (!childDelete) {
            candidates.push(change);
          }
        }
      }
    }
    if (candidates.length === 0) {
      return copy;
    }

    // Combine candidates with created directories
    for (const change of changes) {
      if (candidates.length === 0) {
        break;
      }
      if (change.operation === Operation.Create) {
        // If this is an added directory it can be combined with a candidate
        const path = change.path.clone();
        const fromEntry = basicDiff.get(path);
        if (fromEntry !== undefined && fromEntry.from !== undefined && fromEntry.from.type === EntryType.Dir) {
          continue;
        }

        const candidate = candidates.pop()!;
        copy = copy.filter(element => element !== candidate && element !== change);
        let operation: Operation;
        const candidateParentPath = candidate.path.clone();
        candidateParentPath.pop();
        const changeParentPath = change.path.clone();
        changeParentPath.pop();
        if (candidateParentPath.isEqualTo(changeParentPath)) {
          operation = Operation.Rename;
        } else {
          operation = Operation.Move;
        }
        copy.push({ path: change.path, operation, origins: [candidate.path] });
      }
    }
    return copy;
  }

  static directoryCopies(basicDiff: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;
    for (const change of changes) {
      const entry = basicDiff.get(change.path);
      if (change.operation === Operation.Copy && entry !== undefined && entry.to !== undefined && entry.to.type === EntryType.Dir) {
        const path = change.path.clone();
        for (const child of changes) {
          if (child.operation === Operation.Copy) {
            const childParentPath = child.path.clone();
            childParentPath.pop();
            if (childParentPath.isEqualTo(path)) {
              copy = copy.filter(element => element !== child);
            }
          }
        }
      }
    }
    return copy;
  }

  static directoryMoves(basicDiff: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;
    for (const change of changes) {
      const entry = basicDiff.get(change.path);
      if (entry === undefined || (entry !== undefined && entry.to === undefined && entry.from === undefined)) {
        throw new Error('entry.to and entry.from cannot be undefined for a move');
      }
      const entryPtr = entry.to !== undefined ? entry.to! : entry.from!;
      if (change.operation === Operation.Move && entryPtr.type === EntryType.Dir) {
        const path = change.path.clone();
        for (const child of changes) {
          if (child.operation === Operation.Move) {
            const childParentPath = child.path.clone();
            childParentPath.pop();
            if (childParentPath.isEqualTo(path)) {
              copy = copy.filter(element => element !== child);
            }
          }
        }
      }
    }
    return copy;
  }

  static directoryRemoves(basicDiff: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;
    for (const change of changes) {
      const entry = basicDiff.get(change.path);
      if (change.operation === Operation.Delete && entry !== undefined && entry.from !== undefined && entry.from.type === EntryType.Dir) {
        const path = change.path.clone();
        for (const child of changes) {
          if (child.operation === Operation.Delete) {
            const childParentPath = child.path.clone();
            childParentPath.pop();
            if (childParentPath.isEqualTo(path)) {
              copy = copy.filter(element => element !== child);
            }
          }
        }
      }
    }
    return copy;
  }

  static removeRedundantOperations(basicDiff: Map<Path, { from?: Entry; to?: Entry }>, changes: Result[]): Result[] {
    let copy = changes;
    copy = this.renameParentDirectory(basicDiff, changes);

    // Call directoryRenames before directoryRemoves, otherwise
    // directoryRenames thinks the directory removes are childless.
    copy = this.directoryRenames(basicDiff, copy);
    copy = this.directoryCopies(basicDiff, copy);
    copy = this.directoryMoves(basicDiff, copy);
    copy = this.directoryRemoves(basicDiff, copy);
    return copy;
  }

  // Generate a diff between two dirs. It will try to find the following changes:
  // Create, Delete, Change, Touch, Move, Copy and Rename. This system is not
  // fully waterproof because the actual changes that a user make could differ.
  // For instance coping a file and changing the content in one project version
  // is analized by this method as the creation of a new file because it could
  // not extract the copy-operation from the original dir.
  static async diff(from: Dir, to: Dir): Promise<Result[]> {
    if (from.key && to.key && from.key.isEqual(to.key)) {
      return [];
    }

    // Get the naive diff and find alternate origins.
    const basicDiff: Map<Path, { from?: Entry; to?: Entry }> = await this.naiveDiff(from, to);

    // Collection of origins which are required to conclude if a file is created
    // or moved/copied from another location in the file structure.
    const origins: Map<string, Path[]> = await this.originHash(from, new Path(), basicDiff);

    // Sets to keep track of what happened.
    const preprocessed = this.dirPreprocess(basicDiff);
    const newContentKeys = preprocessed[0];
    const newPaths = preprocessed[4];
    const renamedPaths = preprocessed[1];
    const removedPaths = preprocessed[2];
    let changes = preprocessed[3];
    let copies: Map<StreamKey, [Path]> = new Map();

    if (newPaths.size > 0) {
      copies = await this.findCopies(newPaths, to, new Path());
    }

    for (const [encryptedPath, value] of basicDiff) {
      // Ignore entries that have been renamed.
      if (renamedPaths.includes(encryptedPath)) {
        continue;
      }

      const fromEntry = value.from;
      const toEntry = value.to;
      let originFilePaths = this.gatherOrigins(origins, fromEntry, toEntry);
      let operation = Operation.Undefined;
      if (originFilePaths.length === 0) {
        operation = this.operationWithoutOrigins(newContentKeys, fromEntry, toEntry);
        if (operation === Operation.Move) {
          // We ignore file removes when part of a move operation.
          continue;
        }
        if (operation === Operation.Create) {
          if (toEntry !== undefined) {
            const streamKey = toEntry.key;
            if (streamKey !== undefined && copies.has(streamKey)) {
              operation = Operation.Copy;
              const originPaths = copies.get(streamKey);
              if (originPaths !== undefined) {
                originFilePaths = originPaths;
              } else {
                originFilePaths = [];
              }
            }
          }
        }
      } else if (toEntry === undefined && this.isMove(newContentKeys, fromEntry)) {
        operation = Operation.Move;
      } else if (toEntry === undefined && !this.isMove(newContentKeys, fromEntry)) {
        operation = Operation.Delete;
      } else {
        if (toEntry === undefined) {
          continue;
        }

        if (this.isOverlapping(originFilePaths, removedPaths)) {
          // Filter the overlapping removedPaths from the originFilePaths.
          originFilePaths = this.filterNonOverlapping(originFilePaths, removedPaths);
          if (originFilePaths.length === 0) {
            continue;
          }

          // If the 'move' happened in the same parent directory then it's a
          // 'rename'.
          operation = Operation.Move;
          if (originFilePaths.length === 1) {
            const origin = originFilePaths.at(0)!;
            try {
              const destinationDirectory = encryptedPath.clone();
              destinationDirectory.pop();
              const toDirectory = origin.clone();
              toDirectory.pop();
              if (destinationDirectory === toDirectory) {
                operation = Operation.Rename;
              }
            } catch (error) {
              logger.warn('Is this a rename/move of the root dir?');
            }
          } else {
            logger.warn('There are more than one originFilePaths.');
          }
        } else {
          operation = Operation.Create;
          if (toEntry !== undefined && originFilePaths.length !== 0) {
            const originPath = originFilePaths.pop()!;
            const compareEntry = basicDiff.get(originPath);
            if (
              compareEntry !== undefined &&
              compareEntry.to !== undefined &&
              toEntry.size === compareEntry.to.size &&
              toEntry.type === compareEntry.to.type
            ) {
              operation = Operation.Copy;
            } else if (
              compareEntry !== undefined &&
              compareEntry.from !== undefined &&
              compareEntry.to === undefined &&
              toEntry.size === compareEntry.from.size &&
              toEntry.type === compareEntry.from.type
            ) {
              operation = Operation.Move;
            }
          }
          const streamKey = toEntry.key;
          if (streamKey !== undefined && copies.has(streamKey)) {
            operation = Operation.Copy;
            const originPaths = copies.get(streamKey);
            if (originPaths !== undefined) {
              originFilePaths = originPaths;
            } else {
              originFilePaths = [];
            }
          }
        }
      }

      if (operation === Operation.Undefined) {
        logger.warn('Undefined change found. Ignoring: %s', encryptedPath.toEncryptedString());
      } else {
        changes.push({ path: encryptedPath, operation, origins: originFilePaths });
      }
    }

    changes = this.removeRedundantOperations(basicDiff, changes);
    return changes;
  }

  // Attempts to extract the longest common path from 2 path strings.
  // If nothing is in common, the root (/) will be returned.
  static commonPath(a: Path, b: Path): Path {
    const commonLength = Math.min(a.length(), b.length());
    for (let i = 0; i < commonLength; i++) {
      if (!equal(a.segments[i], b.segments[i])) {
        return new Path(a.segments.slice(0, i));
      }
    }
    return new Path(a.segments.slice(0, commonLength));
  }

  // Creates a summary of a project version. The summary is a grouping of
  // operations that happened in the longest common folder.
  static summarize(diffArray: Result[], maxResults?: number): Summary[] {
    const diffByOperation = new Map<Operation, Result[]>();

    // Group all diff results by operation.
    for (const diff of diffArray) {
      const cat = diffByOperation.get(diff.operation);
      diffByOperation.set(diff.operation, cat ? cat.concat(diff) : [diff]);
    }

    const res: Summary[] = [];
    for (const [operation, results] of diffByOperation) {
      let sharedPath: Path | undefined = undefined;
      // Squash the change to one common path.
      for (const diff of results) {
        sharedPath = sharedPath ? this.commonPath(sharedPath, diff.path) : diff.path;
      }
      if (maxResults && results.length > maxResults) {
        res.push({
          path: sharedPath ?? new Path(),
          operation,
          results: results.slice(0, maxResults),
          cutoffCount: results.length - maxResults,
        });
      } else {
        res.push({ path: sharedPath ?? new Path(), operation, results, cutoffCount: 0 });
      }
    }

    return res;
  }
}
